import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useCreateOrUpdateDiscount } from '~entities/discount/api/create-update-discount';
import { useGetDiscount } from '~entities/discount/api/get-discount';

import { DiscountRulesSelector } from '~pages/settings/discounts/discount-rules';
import { ExcludedClassesTable } from '~pages/settings/discounts/excluded-classes-table';

import { useGetCurrency } from '~shared/api/currency-api';
import { AmountInput } from '~shared/components/discounts/amount-input';
import { DiscountSelect } from '~shared/components/discounts/discount-select';

type DiscountType = 'flat' | 'by-student' | 'amount-by-student' | 'by-number';

interface StudentDiscount {
  id: number;
  value: string;
}

interface AmountDiscount {
  id: number;
  value: string;
}

export function MultiStudentDiscount({
  discountRules,
  setDiscountRules,
}: {
  discountRules: string | null;
  setDiscountRules: (rules: string | null) => void;
}) {
  const { data: discountData } = useGetDiscount('multi-student');
  const { data: currencyDetails } = useGetCurrency();
  const updateDiscountMutation = useCreateOrUpdateDiscount();
  const [isActive, setIsActive] = useState(false);
  const [discountType, setDiscountType] = useState<DiscountType>('flat');
  const [flatDiscount, setFlatDiscount] = useState('0');
  const [studentDiscounts, setStudentDiscounts] = useState<
    Array<StudentDiscount>
  >([{ id: 1, value: '0' }]);
  const [amountDiscounts, setAmountDiscounts] = useState<Array<AmountDiscount>>(
    [{ id: 1, value: '0' }],
  );

  const [studentExcludedClasses, setStudentExcludedClasses] = useState<
    Array<string>
  >([]);

  useEffect(() => {
    if (discountData) {
      setIsActive(discountData.isActive);
      setDiscountRules(discountData.discountRules);
      setStudentExcludedClasses(discountData.excludedClasses ?? []);

      // Set the discount type based on the API response
      if (discountData.type === 'flat') {
        setDiscountType('flat');
        setFlatDiscount(discountData.flat ? discountData.flat.toString() : '0');
      } else if (discountData.type === 'by-student') {
        setDiscountType('by-student');
        const byStudentDiscounts = Object.entries(
          discountData.byStudent ?? {},
        ).map(([id, value]) => ({
          id: Number(id),
          value: value ? value.toString() : '0',
        }));
        setStudentDiscounts(byStudentDiscounts);
      } else if (discountData.type === 'amount-by-student') {
        setDiscountType('amount-by-student');
        const amountByStudentDiscounts = Object.entries(
          discountData.amountByStudent ?? {},
        ).map(([id, value]) => ({
          id: Number(id),
          value: value ? value.toString() : '0',
        }));
        setAmountDiscounts(amountByStudentDiscounts);
      }
    }
  }, [discountData]);

  const handleAddMore = () => {
    const nextId =
      (discountType === 'amount-by-student'
        ? amountDiscounts
        : studentDiscounts
      ).length + 1;

    if (discountType === 'amount-by-student') {
      setAmountDiscounts([...amountDiscounts, { id: nextId, value: '0.00' }]);
    } else {
      setStudentDiscounts([...studentDiscounts, { id: nextId, value: '0' }]);
    }
  };

  const handleAmountChange = (id: number, value: string) => {
    setAmountDiscounts((prevDiscounts) =>
      prevDiscounts.map((discount) =>
        discount.id === id ? { ...discount, value } : discount,
      ),
    );
  };

  const handleDiscountChange = (id: number, value: string) => {
    setStudentDiscounts((prevDiscounts) =>
      prevDiscounts.map((discount) =>
        discount.id === id ? { ...discount, value } : discount,
      ),
    );
  };

  const handleRemoveDiscount = (id: number) => {
    if (discountType === 'amount-by-student') {
      setAmountDiscounts((prevDiscounts) => {
        const filteredDiscounts = prevDiscounts.filter(
          (discount) => discount.id !== id,
        );
        return filteredDiscounts.map((discount, index) => ({
          ...discount,
          id: index + 1, // Reindex starting from 1
        }));
      });
    } else if (discountType === 'by-student') {
      setStudentDiscounts((prevDiscounts) => {
        const filteredDiscounts = prevDiscounts.filter(
          (discount) => discount.id !== id,
        );
        return filteredDiscounts.map((discount, index) => ({
          ...discount,
          id: index + 1, // Reindex starting from 1
        }));
      });
    }
  };

  const handleSubmit = () => {
    if (!discountRules) {
      toast.error('Please select discount rules');
      return;
    }
    const discountDataPayload = {
      type: discountType,
      excludedClasses: studentExcludedClasses,
      discountRules: discountRules,
      category: 'multi-student',
      isActive,
      ...(discountType === 'flat'
        ? {
            flat: Number(flatDiscount),
          }
        : discountType === 'by-student'
          ? {
              byStudent: studentDiscounts.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.id]: Number(curr.value),
                }),
                {},
              ),
            }
          : {
              amountByStudent: amountDiscounts.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.id]: Number(curr.value),
                }),
                {},
              ),
            }),
    };

    if (discountData && Object.keys(discountData).length > 0) {
      updateDiscountMutation.mutate({
        ...discountDataPayload,
        action: 'update',
      });
    } else {
      updateDiscountMutation.mutate({
        ...discountDataPayload,
        action: 'create',
      });
    }
  };

  const getOrdinalLabel = (num: number) => {
    const ordinals = [
      'First',
      'Second',
      'Third',
      'Fourth',
      'Fifth',
      'Sixth',
      'Seventh',
      'Eighth',
      'Ninth',
      'Tenth',
    ];
    return ordinals[num - 1] ?? `${num}th`;
  };

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        {/* Discount Type Selection */}
        <div className='space-y-4'>
          <label className='block text-sm font-medium text-gray-700'>
            Discount Type
          </label>
          <div className='inline-flex rounded-lg bg-gray-50 p-1 shadow-sm'>
            {[
              { value: 'flat', label: 'Flat %' },
              { value: 'by-student', label: '% by Student' },
              {
                value: 'amount-by-student',
                label: `${currencyDetails?.symbol || '$'} by Student`,
              },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setDiscountType(option.value as DiscountType);
                }}
                className={`relative rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  (discountData?.type === 'flat' && option.value === 'flat') ||
                  (discountData?.type === 'by-student' &&
                    option.value === 'by-student') ||
                  (discountData?.type === 'amount-by-student' &&
                    option.value === 'amount-by-student')
                    ? 'bg-yellow-300 text-gray-900 shadow'
                    : discountType === option.value
                      ? 'bg-white text-gray-900 shadow'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active Toggle */}
        <div className='inline-flex items-center justify-between rounded-lg border bg-white p-3 shadow-sm'>
          <div className='flex items-center space-x-3'>
            <div className='flex flex-col'>
              <span className='text-sm text-gray-500'>
                {isActive
                  ? 'Multi-Student Discount is currently active'
                  : 'Multi-Student Discount is currently inactive'}
              </span>
            </div>
          </div>
          <div className='ml-8'>
            <button
              type='button'
              onClick={() => {
                setIsActive(!isActive);
              }}
              className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                isActive ? 'bg-green-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block size-7 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isActive ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Discount Configuration */}
      <div className='rounded-lg border border-gray-200 bg-gray-50 p-6'>
        {discountType === 'flat' && (
          <div className='max-w-md'>
            <DiscountSelect
              label='Multi-Student Discount'
              value={flatDiscount}
              onChange={setFlatDiscount}
            />
            <p className='mt-2 text-sm text-gray-500'>
              Flat rate applies the discount to every student linked to a parent
              account, including the first student even if only one student is
              enrolled.
            </p>
          </div>
        )}

        {discountType === 'by-student' && (
          <div className='space-y-6'>
            <div className='max-w-2xl space-y-3'>
              {studentDiscounts.map((discount) => (
                <div
                  key={discount.id}
                  className='flex items-center justify-between gap-2'
                >
                  <div className='grow'>
                    <DiscountSelect
                      label={`Discount for ${getOrdinalLabel(discount.id)} Child`}
                      value={discount.value}
                      onChange={(value) => {
                        handleDiscountChange(discount.id, value);
                      }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      handleRemoveDiscount(discount.id);
                    }}
                    className='self-center hover:text-red-700'
                  >
                    <X className='size-4' />
                  </button>
                </div>
              ))}
            </div>

            {studentDiscounts.length < 10 && (
              <button
                onClick={handleAddMore}
                className='mt-4 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2'
              >
                Add Another Child
              </button>
            )}
          </div>
        )}

        {discountType === 'amount-by-student' && (
          <div className='space-y-6'>
            <div className='max-w-2xl space-y-3'>
              {amountDiscounts.map((discount) => (
                <div
                  key={discount.id}
                  className='flex items-center justify-between gap-2'
                >
                  <div className='grow'>
                    <AmountInput
                      label={`Discount for ${getOrdinalLabel(discount.id)} Child`}
                      value={discount.value}
                      onChange={(value) => {
                        handleAmountChange(discount.id, value);
                      }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      handleRemoveDiscount(discount.id);
                    }}
                    className='self-center hover:text-red-700'
                  >
                    <X className='size-4' />
                  </button>
                </div>
              ))}
            </div>

            {amountDiscounts.length < 10 && (
              <button
                onClick={handleAddMore}
                className='mt-4 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2'
              >
                Add Another Child
              </button>
            )}
          </div>
        )}
      </div>

      <DiscountRulesSelector
        selectedRules={discountRules}
        setSelectedRules={setDiscountRules}
      />
      <ExcludedClassesTable
        selectedClasses={studentExcludedClasses}
        setSelectedClasses={setStudentExcludedClasses}
      />

      {/* Submit Button */}
      <div className='flex justify-end'>
        <button
          onClick={handleSubmit}
          className='inline-flex justify-center rounded-md border border-transparent bg-yellow-400 px-4 py-2 text-sm font-medium text-black shadow-sm hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2'
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}
